import apiClient from '@/core/api/client';
import { API_ENDPOINTS } from '@/core/api/endpoints';
import { ParentInviteRequest, ParentResponse, ParentListResponse, StudentAddRequest, StudentResponse, StudentListResponse } from '../types';

/**
 * Parent API Service
 */
export const parentApi = {
    /**
     * Invite a new parent
     */
    inviteParent: async (schoolSlug: string, data: ParentInviteRequest): Promise<ParentResponse> => {
        try {
            const response = await apiClient.post<ParentResponse>(API_ENDPOINTS.PARENTS.INVITE(schoolSlug), data);
            return response.data;
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.error || error.response?.data?.message || 'Failed to add parent'
            };
        }
    },

    /**
     * Get all parents for a school (with search, pagination, and sorting)
     */
    getParents: async (
        schoolSlug: string,
        params?: {
            page?: number;
            page_size?: number;
            search?: string;
            sort_by?: string;
            sort_order?: 'asc' | 'desc';
        }
    ): Promise<ParentListResponse> => {
        try {
            const cleanParams: Record<string, any> = {};
            if (params) {
                if (params.page !== undefined) cleanParams.page = params.page;
                if (params.page_size !== undefined) cleanParams.page_size = params.page_size;
                if (params.search && params.search.trim()) cleanParams.search = params.search.trim();
                if (params.sort_by) cleanParams.sort_by = params.sort_by;
                if (params.sort_order) cleanParams.sort_order = params.sort_order;
            }
            const response = await apiClient.get<ParentListResponse>(API_ENDPOINTS.PARENTS.LIST(schoolSlug), { params: cleanParams });
            return response.data;
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.error || error.response?.data?.message || 'Failed to fetch parents'
            };
        }
    },

    /**
     * Get all students for a school (with search, pagination, and sorting)
     */
    getStudents: async (
        schoolSlug: string,
        params?: {
            page?: number;
            page_size?: number;
            search?: string;
            sort_by?: string;
            sort_order?: 'asc' | 'desc';
        }
    ): Promise<StudentListResponse> => {
        try {
            const cleanParams: Record<string, any> = {};
            if (params) {
                if (params.page !== undefined) cleanParams.page = params.page;
                if (params.page_size !== undefined) cleanParams.page_size = params.page_size;
                if (params.search && params.search.trim()) cleanParams.search = params.search.trim();
                if (params.sort_by) cleanParams.sort_by = params.sort_by;
                if (params.sort_order) cleanParams.sort_order = params.sort_order;
            }
            const response = await apiClient.get<StudentListResponse>(API_ENDPOINTS.STUDENTS.LIST(schoolSlug), { params: cleanParams });
            return response.data;
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.error || error.response?.data?.message || 'Failed to fetch students'
            };
        }
    },

    /**
     * Update a parent
     */
    updateParent: async (
        schoolSlug: string,
        userId: string,
        data: {
            name?: string;
            email?: string;
            phone_number?: string;
            address?: string;
            secondary_phone?: string;
        }
    ): Promise<ParentResponse> => {
        try {
            const response = await apiClient.patch<ParentResponse>(API_ENDPOINTS.PARENTS.UPDATE(schoolSlug, userId), data);
            return response.data;
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.error || error.response?.data?.message || 'Failed to update parent'
            };
        }
    },

    /**
     * Delete a parent
     */
    deleteParent: async (schoolSlug: string, userId: string): Promise<any> => {
        try {
            const response = await apiClient.delete(API_ENDPOINTS.PARENTS.DELETE(schoolSlug, userId));
            return response.data;
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.error || error.response?.data?.message || 'Failed to delete parent'
            };
        }
    },

    /**
     * Add a child to a parent
     */
    addStudent: async (schoolSlug: string, data: StudentAddRequest): Promise<StudentResponse> => {
        try {
            const response = await apiClient.post<StudentResponse>(API_ENDPOINTS.STUDENTS.CREATE(schoolSlug), data);
            return response.data;
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.error || error.response?.data?.message || 'Failed to add student'
            };
        }
    },

    /**
     * Search student by admission number
     */
    searchStudentByAdmission: async (schoolSlug: string, admissionNumber: string): Promise<any> => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.STUDENTS.SEARCH(schoolSlug), {
                params: { admission_number: admissionNumber }
            });
            return response.data;
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.error || error.response?.data?.message || 'Failed to search student'
            };
        }
    },

    /**
     * Assign/unassign student to parent
     */
    assignStudentParent: async (
        schoolSlug: string,
        data: {
            student_id: string;
            parent_user_id: string;
            action: 'link' | 'unlink' | 'delink';
        }
    ): Promise<any> => {
        try {
            const response = await apiClient.post(API_ENDPOINTS.PARENTS.ASSIGNMENT(schoolSlug), data);
            return response.data;
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.error || error.response?.data?.message || 'Failed to update assignment'
            };
        }
    },


    /**
     * Update a student
     */
    updateStudent: async (
        schoolSlug: string,
        studentId: string,
        data: {
            name?: string;
            class_name?: string;
            class?: string;
            admission_number?: string;
            section?: string;
            dob?: string;
            roll_number?: string;
            gender?: string;
            session?: string;
            parent_id?: string;
        }
    ): Promise<StudentResponse> => {
        try {
            const response = await apiClient.patch<StudentResponse>(API_ENDPOINTS.STUDENTS.UPDATE(schoolSlug, studentId), data);
            return response.data;
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.error || error.response?.data?.message || 'Failed to update student'
            };
        }
    },

    /**
     * Delete a student
     */
    deleteStudent: async (schoolSlug: string, studentId: string): Promise<any> => {
        try {
            const response = await apiClient.delete(API_ENDPOINTS.STUDENTS.DELETE(schoolSlug, studentId));
            return response.data;
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.error || error.response?.data?.message || 'Failed to delete student'
            };
        }
    },

    /**
     * Download import template
     */
    downloadTemplate: async (schoolSlug: string, type: string): Promise<any> => {
        try {
            const response = await apiClient.get(
                API_ENDPOINTS.IMPORT_EXPORT.DOWNLOAD_TEMPLATE(schoolSlug, type),
                { responseType: 'blob' }
            );
            return {
                success: true,
                data: response.data,
                headers: response.headers
            };
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.error || error.response?.data?.message || 'Failed to download template'
            };
        }
    },

    /**
     * Import students-parents from file
     */
    importFile: async (schoolSlug: string, type: string, file: File): Promise<any> => {
        try {
            const formData = new FormData();
            formData.append('file', file);
            
            const response = await apiClient.post(
                API_ENDPOINTS.IMPORT_EXPORT.IMPORT_FILE(schoolSlug, type),
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );
            return response.data;
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.error || error.response?.data?.message || 'Failed to import file'
            };
        }
    }
};
